import { HttpRequest, HttpHeaders, HttpEvent, HttpErrorResponse, HttpHandlerFn } from "@angular/common/http";
import { Observable, throwError, catchError, switchMap, from } from "rxjs";
import { environment } from "environments/environment";
import { inject } from "@angular/core";
import { UserStore } from "../store/user.store";

const isCorsError = (error: HttpErrorResponse): boolean =>
  error instanceof HttpErrorResponse && !error.ok && error.status === 0 && error.error instanceof ProgressEvent;

export const authInterceptor = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
  const userStore = inject(UserStore);
  const token = userStore.accessToken?.();

  // Skip auth header for assets or external requests
  if (!req.url.startsWith(environment.baseUrl)) {
    return next(req);
  }

  // Add interaction ID and auth token if available
  let headers = new HttpHeaders().set("interaction-id", crypto.randomUUID());
  if (token) {
    headers = headers.set("Authorization", `Bearer ${token}`);

    // Only set Content-Type for non-FormData requests
    if (!(req.body instanceof FormData)) {
      headers = headers.set("Content-Type", "application/json");
    }
  }

  const authReq = req.clone({ headers });

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // Log server errors
      if (error.status >= 500) {
        console.error("Server error:", error);
      }

      // Handle CORS errors
      if (isCorsError(error)) {
        return throwError(() => new Error("CORS Error"));
      }

      // Handle unauthorized errors and token refresh
      if (error.status === 401) {
        const refreshToken = userStore.refreshToken?.();

        if (!refreshToken) {
          void userStore.logout();
          return throwError(() => new Error("No refresh token available"));
        }

        // Attempt to refresh the token
        return from(Promise.resolve(userStore.refreshToken())).pipe(
          switchMap(() => {
            // Retry the request with new token
            const newToken = userStore.accessToken?.();
            if (!newToken) {
              return throwError(() => new Error("Token refresh failed"));
            }

            const newHeaders = headers.set("Authorization", `Bearer ${newToken}`);
            const retryReq = req.clone({ headers: newHeaders });
            return next(retryReq);
          }),
          catchError((refreshError: Error) => {
            console.error("Token refresh failed:", refreshError);
            void userStore.logout();
            return throwError(() => new Error("Token refresh failed"));
          }),
        );
      }

      return throwError(() => (error instanceof Error ? error : new Error(error.message)));
    }),
  );
};
